// pages/ChatSpace/ChatSpaceMessageBubble.js
import React, { useMemo, useRef, useState } from 'react';
import { Box, IconButton, Avatar, Typography, Tooltip, CircularProgress } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArticleIcon from '@mui/icons-material/Article';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { marked } from 'marked';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import ChartRenderer from './ChartRenderer';

const ChatSpaceMessageBubble = React.memo(({ msg, settings, onCitations, onCopy }) => {
    const messageContentRef = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);

    // Parse message to extract chart blocks and regular text
    const parseMessageWithCharts = useMemo(() => {
        if (!msg.message) return { parts: [], hasCharts: false };

        const text = msg.message;
        const parts = [];
        // Match ```chart ... ``` blocks
        const chartRegex = /```chart\s*([\s\S]*?)```/g;
        let lastIndex = 0;
        let match;
        let hasCharts = false;

        while ((match = chartRegex.exec(text)) !== null) {
            // Add text before the chart
            if (match.index > lastIndex) {
                parts.push({
                    type: 'text',
                    content: text.slice(lastIndex, match.index),
                });
            }

            // Try to parse the chart JSON
            try {
                const chartConfig = JSON.parse(match[1].trim());
                parts.push({
                    type: 'chart',
                    config: chartConfig,
                });
                hasCharts = true;
            } catch (e) {
                // If JSON parsing fails, treat as regular code block
                parts.push({
                    type: 'text',
                    content: match[0],
                });
            }

            lastIndex = match.index + match[0].length;
        }

        // Add remaining text after last chart
        if (lastIndex < text.length) {
            parts.push({
                type: 'text',
                content: text.slice(lastIndex),
            });
        }

        // If no charts found, return the whole message as text
        if (parts.length === 0) {
            parts.push({ type: 'text', content: text });
        }

        return { parts, hasCharts };
    }, [msg.message]);

    const createMarkup = (text) => {
        try {
            const rawMarkup = marked.parse(text || '');
            return { __html: rawMarkup };
        } catch {
            return { __html: text || '' };
        }
    };

    // Download generated image
    const handleDownloadImage = () => {
        if (!msg.generated_image) return;

        try {
            // Create a link element and trigger download
            const link = document.createElement('a');
            link.href = `data:image/png;base64,${msg.generated_image}`;
            link.download = `generated_image_${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading image:', error);
        }
    };

    // Download response as PDF
    const handleDownloadPDF = async () => {
        if (!messageContentRef.current) return;

        setIsDownloading(true);
        try {
            const container = messageContentRef.current;

            // Capture the message content as canvas with HIGH QUALITY settings
            const canvas = await html2canvas(container, {
                scale: 3, // Higher scale for HD quality
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                width: container.scrollWidth,
                height: container.scrollHeight,
                scrollX: 0,
                scrollY: 0,
                windowWidth: container.scrollWidth,
                windowHeight: container.scrollHeight,
                imageTimeout: 0,
                allowTaint: true,
            });

            const imgData = canvas.toDataURL('image/png', 1.0);

            // A4 dimensions in points (72 DPI)
            const a4Width = 595.28;
            const a4Height = 841.89;
            const margin = 40;
            const contentWidth = a4Width - (margin * 2);

            // Calculate scaled dimensions to fit A4 width
            const imgAspectRatio = canvas.height / canvas.width;
            const scaledWidth = contentWidth;
            const scaledHeight = contentWidth * imgAspectRatio;

            // Calculate total page height needed
            const headerHeight = 80;
            const totalContentHeight = scaledHeight + headerHeight + margin;

            // Create PDF - use A4 or custom height if content is longer
            const pageHeight = Math.max(a4Height, totalContentHeight + margin);

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'pt',
                format: [a4Width, pageHeight],
                compress: true,
            });

            // Add header
            pdf.setFontSize(18);
            pdf.setTextColor(51, 51, 51);
            pdf.text('Chat Space Response', margin, 35);

            // Add timestamp
            pdf.setFontSize(10);
            pdf.setTextColor(128, 128, 128);
            pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, 55);

            // Add line separator
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.5);
            pdf.line(margin, 65, a4Width - margin, 65);

            // Add the message image with proper sizing
            pdf.addImage(imgData, 'PNG', margin, headerHeight, scaledWidth, scaledHeight, undefined, 'FAST');

            // Save PDF
            const fileName = `chatspace_response_${Date.now()}.pdf`;
            pdf.save(fileName);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const markdownStyles = {
        fontSize: '0.875rem',
        lineHeight: 1.7,
        color: 'text.primary',
        '& p': { mt: 0, mb: 1 },
        '& pre': {
            bgcolor: 'grey.100',
            borderRadius: 1,
            p: 1.5,
            overflow: 'auto',
            fontSize: '0.8rem',
        },
        '& code': { fontSize: '0.8rem', bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5 },
        '& a': { color: 'primary.main' },
        '& ul, & ol': { pl: 3, mb: 1 },
        // Table styles for Analytics mode
        '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            mb: 2,
            fontSize: '0.8rem',
        },
        '& th, & td': {
            border: '1px solid',
            borderColor: 'grey.300',
            p: 1,
            textAlign: 'left',
        },
        '& th': {
            bgcolor: 'grey.100',
            fontWeight: 600,
        },
        '& blockquote': {
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            pl: 2,
            ml: 0,
            color: 'text.secondary',
            fontStyle: 'italic',
        },
    };

    const renderBotMessage = () => {
        const { parts, hasCharts } = parseMessageWithCharts;

        return (
            <>
                {/* Render text/chart content */}
                {!hasCharts ? (
                    <Box sx={markdownStyles} dangerouslySetInnerHTML={createMarkup(msg.message)} />
                ) : (
                    parts.map((part, index) => {
                        if (part.type === 'chart') {
                            return <ChartRenderer key={`chart-${index}`} config={part.config} />;
                        }
                        return (
                            <Box
                                key={`text-${index}`}
                                sx={markdownStyles}
                                dangerouslySetInnerHTML={createMarkup(part.content)}
                            />
                        );
                    })
                )}

                {/* Render generated image if present */}
                {msg.generated_image && (
                    <Box sx={{ mt: 2, position: 'relative' }}>
                        <Box
                            sx={{
                                position: 'relative',
                                display: 'inline-block',
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'grey.200',
                                '&:hover .image-download-btn': {
                                    opacity: 1,
                                },
                            }}
                        >
                            <img
                                src={`data:image/png;base64,${msg.generated_image}`}
                                alt="Generated"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: 400,
                                    display: 'block',
                                    borderRadius: 8,
                                }}
                            />
                            <Tooltip title="Download image">
                                <IconButton
                                    className="image-download-btn"
                                    onClick={handleDownloadImage}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 8,
                                        right: 8,
                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                        color: 'white',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                        '&:hover': {
                                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                                        },
                                    }}
                                >
                                    <DownloadIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                )}

                {/* Show error if image generation failed */}
                {msg.image_generation_error && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: 'error.light',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <BrokenImageIcon sx={{ color: 'error.main' }} />
                        <Typography variant="body2" color="error.main">
                            Image generation failed: {msg.image_generation_error}
                        </Typography>
                    </Box>
                )}
            </>
        );
    };

    // User messages: right-aligned with bubble style
    if (msg.is_from_user) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    px: 2,
                    py: 1.5,
                }}
            >
                <Box
                    sx={{
                        maxWidth: '70%',
                        display: 'flex',
                        flexDirection: 'row-reverse',
                        gap: 1.5,
                    }}
                >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', flexShrink: 0 }}>
                        <PersonIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            borderRadius: 3,
                            borderTopRightRadius: 0.5,
                            px: 2,
                            py: 1.5,
                        }}
                    >
                        {/* Show attached image if present */}
                        {msg.attached_image && (
                            <Box sx={{ mb: 1.5 }}>
                                <img
                                    src={msg.attached_image}
                                    alt="Attached"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: 150,
                                        borderRadius: 8,
                                        display: 'block',
                                    }}
                                />
                            </Box>
                        )}
                        <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                        >
                            {msg.message}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    // Bot messages: left-aligned
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                '&:hover': { bgcolor: 'action.hover' },
                '&:hover .action-buttons': { opacity: 1 },
                transition: 'background-color 0.15s',
            }}
        >
            <Box
                sx={{
                    maxWidth: '85%',
                    display: 'flex',
                    gap: 1.5,
                }}
            >
                <Avatar
                    sx={{
                        width: 32,
                        height: 32,
                        bgcolor: settings.primary_color || '#3b82f6',
                        color: settings.secondary_color || '#fff',
                        flexShrink: 0,
                    }}
                >
                    <SmartToyIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Message content wrapper for PDF capture */}
                    <Box ref={messageContentRef} sx={{ bgcolor: 'transparent' }}>
                        {renderBotMessage()}
                    </Box>
                    {/* Action buttons for AI messages */}
                    <Box
                        className="action-buttons"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5,
                            opacity: 0,
                            transition: 'opacity 0.15s',
                        }}
                    >
                        <Tooltip title="Copy message">
                            <IconButton
                                size="small"
                                onClick={() => onCopy(msg.message)}
                                sx={{ p: 0.5 }}
                            >
                                <ContentCopyIcon sx={{ fontSize: 16, color: 'grey.500' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Download as PDF">
                            <IconButton
                                size="small"
                                onClick={handleDownloadPDF}
                                disabled={isDownloading}
                                sx={{ p: 0.5 }}
                            >
                                {isDownloading ? (
                                    <CircularProgress size={16} sx={{ color: 'grey.500' }} />
                                ) : (
                                    <PictureAsPdfIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                )}
                            </IconButton>
                        </Tooltip>
                        {msg.citations && msg.citations.length > 0 && (
                            <Tooltip title="View sources">
                                <IconButton
                                    size="small"
                                    onClick={() => onCitations(msg)}
                                    sx={{ p: 0.5 }}
                                >
                                    <ArticleIcon sx={{ fontSize: 16, color: 'grey.500' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
});

ChatSpaceMessageBubble.displayName = 'ChatSpaceMessageBubble';

export default ChatSpaceMessageBubble;
